/**
 * Zod-skeemat. Sama skeema validoi lomakkeen selaimessa ja pyynnön
 * palvelimella — yhtenä kappaleena, jotta säännöt eivät pääse eriytymään.
 */
import { z } from 'zod';
import { normalisoiTagit, onSallittuDokumenttiUrl, TAGEJA_ENINTAAN } from './asiakastiedot.js';
import { SAVY_AVAIMET } from './varit.js';
import { LOGO_MAX_BYTES, LOGO_MIME_TYPES, MAX_BRAND_NAME_LENGTH, isLogoMimeType, isLogoVariant, } from './branding.js';
import { CUSTOMER_REPORT_PERIODS, KANBAN_COLUMNS, KANBAN_DUE_CAUSES, KANBAN_RELATION_KINDS, LANE_STATUSES, KANBAN_LANE_NAME_MAX, KANBAN_MILESTONE_DESCRIPTION_MAX, KANBAN_MILESTONE_NAME_MAX, KANBAN_COMMENT_MAX, REACTION_EMOJIS, KANBAN_TASK_DESCRIPTION_MAX, KANBAN_TASK_TITLE_MAX, PARTNER_DESCRIPTION_MAX, PARTNER_NAME_MAX, TASK_ESTIMATES, MAX_BILLABLE_TARGET_SHARE, MAX_DESCRIPTION_LENGTH, MAX_IMPORT_ROWS, MAX_MINUTES_PER_ENTRY, MAX_REMINDER_DAY_OF_MONTH, MAX_SUGGESTION_RATIONALE_LENGTH, MIN_BILLABLE_TARGET_SHARE, MIN_UNLOCK_REASON_LENGTH, MINUTE_STEP, REMINDER_KINDS, REMINDER_SCHEDULE_KIND, } from './constants.js';
import { ATTRIBUTE_KEY_PATTERN, ATTRIBUTE_TARGETS, MAX_ATTRIBUTE_KEY_LENGTH, MAX_ATTRIBUTE_LABEL_LENGTH, MAX_ATTRIBUTE_VALUE_LENGTH, } from './attributes.js';
import { SUPPORTED_CURRENCIES, isCurrencyCode } from './currency.js';
import { ATTRIBUTE_COLUMN_PREFIX, isExportColumnId, parseExportColumns } from './exportColumns.js';
import { APP_LANGUAGES, DEFAULT_LANGUAGE } from './i18n.js';
import { API_TOKEN_ROLES } from './roles.js';
import { EXAM_CHOICE_COUNT, MAX_EXAM_QUESTION_COUNT, MAX_EXAM_SESSION_MINUTES, MAX_EXAM_TIME_LIMIT_MINUTES, MAX_EXPIRY_WARNING_DAYS, MAX_PASS_SCORE_PERCENT, MAX_RETRY_COOLDOWN_MINUTES, MAX_TRAINING_VALIDITY_MONTHS, MIN_EXAM_QUESTION_COUNT, MIN_EXAM_SESSION_MINUTES, MIN_EXAM_TIME_LIMIT_MINUTES, MIN_PASS_SCORE_PERCENT, MIN_TRAINING_VALIDITY_MONTHS, TRAINING_CODE_PATTERN, TRAINING_KEY_PATTERN, TRAINING_STATUSES, } from './trainings.js';
import { isIsoDate, isMonthKey, isTimeOfDay } from './time.js';
import { KEHITYSPOLKU_ASKEL_PATTERN, KEHITYSPOLKU_KOODI_PATTERN, KEHITYSTAVOITE_TILAT, } from './kehitys.js';
import { SERTIFIKAATTI_KOODI_MAX, SERTIFIKAATTI_NIMI_MAX, SERTIFIKAATTI_TARJOAJA_MAX, } from './osaaminen.js';
import { KANBAN_REMINDER_AUDIENCES, KANBAN_REMINDER_NOTE_MAX, KANBAN_REMINDER_PRESETS, } from './taskReminders.js';
// --- Perustyypit -----------------------------------------------------------
export const uuidSchema = z.string().uuid();
export const isoDateSchema = z
    .string()
    .refine(isIsoDate, { message: 'Päivämäärän on oltava muodossa VVVV-KK-PP.' });
export const monthKeySchema = z
    .string()
    .refine(isMonthKey, { message: 'Kuukauden on oltava muodossa VVVV-KK.' });
/**
 * Desimaaliluku merkkijonona. Rahaa ei oteta vastaan liukulukuna, koska
 * JSON-luvun tarkkuus ei ole taattu ja `115.10` voi saapua muodossa
 * `115.09999999999999`.
 */
export const decimalStringSchema = z
    .string()
    .regex(/^-?\d+(\.\d+)?$/, { message: 'Luvun on oltava desimaaliluku, esim. 115.00.' });
export const nonNegativeDecimalSchema = decimalStringSchema.refine((value) => Number(value) >= 0, {
    message: 'Luku ei voi olla negatiivinen.',
});
export const minutesSchema = z
    .number()
    .int({ message: 'Keston on oltava kokonaisluku minuutteja.' })
    .positive({ message: 'Keston on oltava suurempi kuin nolla.' })
    .max(MAX_MINUTES_PER_ENTRY, { message: 'Kesto ei voi ylittää vuorokautta.' })
    .refine((value) => value % MINUTE_STEP === 0, {
    message: 'Kirjaukset tehdään puolen tunnin tarkkuudella.',
});
export const descriptionSchema = z
    .string()
    .trim()
    .max(MAX_DESCRIPTION_LENGTH, {
    message: `Kuvaus voi olla enintään ${MAX_DESCRIPTION_LENGTH} merkkiä.`,
})
    .optional()
    .nullable();
export const roleSchema = z.enum(['ADMIN', 'USER', 'ACCOUNT_MANAGER', 'REPORTER']);
// --- Aikaväli --------------------------------------------------------------
export const dateRangeSchema = z
    .object({ from: isoDateSchema, to: isoDateSchema })
    .refine((range) => range.from <= range.to, {
    message: 'Alkupäivä ei voi olla loppupäivän jälkeen.',
    path: ['from'],
});
// --- Tuntikirjaus ----------------------------------------------------------
/**
 * Huomaa mitä tässä EI ole: `hourlyRate`. Palvelin ratkaisee hinnan
 * periytymisketjusta ja jäädyttää sen riville (suunnitelma 5.2, 5.3).
 * Ainoa paikka, jossa hinta tulee pyynnöstä, on tuonti-API.
 */
export const timeEntryCreateSchema = z.object({
    entryDate: isoDateSchema,
    customerId: uuidSchema,
    taskTypeId: uuidSchema,
    projectId: uuidSchema.nullable().optional(),
    minutes: minutesSchema,
    billable: z.boolean().optional(),
    description: descriptionSchema,
    /** Tehtävä, josta kirjaus kopioitiin (PROJEKTINHALLINTA 16). */
    kanbanTaskId: uuidSchema.nullable().optional(),
});
export const timeEntryUpdateSchema = timeEntryCreateSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Muutettavia kenttiä ei annettu.' });
/**
 * Edellisen päivän kopiointi (suunnitelma 10).
 *
 * Kopio on uusi kirjaus, ei viittaus: hinta ratkaistaan uudelleen
 * kopiointihetken hinnastolla, kuten jokaisella muullakin uudella
 * kirjauksella (5.3).
 */
export const copyDaySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
});
// --- Kuukauden lukitus -----------------------------------------------------
export const monthLockSchema = z.object({
    reason: z.string().trim().max(500).optional().nullable(),
    /**
     * Hinnoittelemattomat laskutettavat kirjaukset estävät lukituksen (6.3).
     * Tämä on tietoinen kuittaus siitä, että ne jäävät hinnattomiksi —
     * lukitsemisen jälkeen niitä ei voi enää korjata avaamatta kuukautta.
     */
    acknowledgeUnpriced: z.boolean().default(false),
});
export const monthUnlockSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(MIN_UNLOCK_REASON_LENGTH, {
        message: `Kerro lyhyesti miksi kuukausi avataan (vähintään ${MIN_UNLOCK_REASON_LENGTH} merkkiä).`,
    })
        .max(500),
});
// --- Tarkistusmerkintä -----------------------------------------------------
export const customerMonthCheckSchema = z.object({
    note: z.string().trim().max(MAX_DESCRIPTION_LENGTH).optional().nullable(),
});
export const checkStatusSchema = z.enum(['UNCHECKED', 'CHECKED', 'STALE']);
// --- Asiakasvastuut --------------------------------------------------------
export const customerAssignmentsSchema = z.object({
    userIds: z.array(uuidSchema),
});
export const userAssignmentsSchema = z.object({
    customerIds: z.array(uuidSchema),
});
// --- Perustiedot -----------------------------------------------------------
/**
 * Koodi on lyhyt, ihmisen luettava tunniste (`ACME`, `PORT26`). Se näkyy
 * kalenterin sirpaleissa ja vientitiedostoissa, joten se normalisoidaan
 * isoiksi kirjaimiksi jo validoinnissa — muuten `acme` ja `ACME` olisivat
 * eri asiakkaita, koska yksikäsitteisyys on kannassa merkkikohtainen.
 */
export const codeSchema = z
    .string()
    .trim()
    .min(2, { message: 'Koodissa on oltava vähintään 2 merkkiä.' })
    .max(20, { message: 'Koodi voi olla enintään 20 merkkiä.' })
    .transform((value) => value.toUpperCase())
    .refine((value) => /^[A-ZÅÄÖ0-9._-]+$/.test(value), {
    message: 'Koodissa saa olla vain kirjaimia, numeroita ja merkkejä . _ -',
});
export const nameSchema = z
    .string()
    .trim()
    .min(2, { message: 'Nimessä on oltava vähintään 2 merkkiä.' })
    .max(200, { message: 'Nimi voi olla enintään 200 merkkiä.' });
/**
 * Asiakkaan kuvaus (markdown).
 *
 * Kenttä on kannassa rajaton teksti, joten raja on tässä ja vain tässä.
 * Kymmenentuhatta merkkiä on noin neljä liuskaa: kuvaus saa kertoa, mitä
 * asiakkuudessa tehdään ja mitä siinä pitää muistaa, muttei olla
 * dokumentti — dokumentit ovat linkkejä (`customerLinkCreateSchema`).
 */
export const notesSchema = z.string().trim().max(10000).optional().nullable();
/** Tuntihinta: valinnainen, ja `null` tarkoittaa "ei omaa hintaa, peritään". */
export const optionalRateSchema = nonNegativeDecimalSchema.nullable().optional();
/** Budjetti ja htp-muunnos ovat aidosti positiivisia — kanta pakottaa saman. */
export const positiveDecimalSchema = decimalStringSchema.refine((value) => Number(value) > 0, {
    message: 'Luvun on oltava suurempi kuin nolla.',
});
export const optionalPositiveDecimalSchema = positiveDecimalSchema.nullable().optional();
/**
 * Laskutettavan työn tavoite osuutena (0–1), esimerkiksi `"0.70"`.
 *
 * Osuutena eikä prosenttilukuna, koska sellaisena se myös lasketaan ja
 * tallennetaan — prosentti on esitysmuoto, ja muunnos kuuluu yhteen
 * paikkaan käyttöliittymässä eikä molempiin päihin rajapintaa.
 */
export const billableTargetShareSchema = decimalStringSchema.refine((value) => Number(value) >= MIN_BILLABLE_TARGET_SHARE && Number(value) <= MAX_BILLABLE_TARGET_SHARE, { message: 'Tavoitteen on oltava välillä 0–100 %.' });
/** `null` = ei omaa tavoitetta, peritään järjestelmän oletus. */
export const optionalBillableTargetShareSchema = billableTargetShareSchema.nullable().optional();
export const projectStatusSchema = z.enum(['PLANNED', 'ACTIVE', 'CLOSED']);
// --- Attribuutit -----------------------------------------------------------
export const attributeTargetSchema = z.enum(ATTRIBUTE_TARGETS);
/**
 * Avain on tekninen tunniste, ei otsikko. Se päätyy CSV:n otsikkoriville
 * ja viennin kyselyparametriin, joten välilyönnit ja ääkköset on suljettu
 * pois — ihmiselle tarkoitettu muoto on `label`.
 */
export const attributeKeySchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2, { message: 'Avaimessa on oltava vähintään 2 merkkiä.' })
    .max(MAX_ATTRIBUTE_KEY_LENGTH, {
    message: `Avain voi olla enintään ${MAX_ATTRIBUTE_KEY_LENGTH} merkkiä.`,
})
    .refine((value) => ATTRIBUTE_KEY_PATTERN.test(value), {
    message: 'Avaimessa saa olla vain pieniä kirjaimia, numeroita ja alaviivoja.',
});
export const attributeLabelSchema = z
    .string()
    .trim()
    .min(1, { message: 'Otsikko ei voi olla tyhjä.' })
    .max(MAX_ATTRIBUTE_LABEL_LENGTH);
/**
 * Muotosääntö talletetaan tekstinä ja käännetään lausekkeeksi vasta
 * käytössä. Kelpaamaton lauseke torjutaan tässä: tallennettuna se
 * kaataisi jokaisen myöhemmän tallennuksen eikä kukaan yhdistäisi virhettä
 * kuukausien takaiseen asetukseen.
 */
export const attributePatternSchema = z
    .string()
    .trim()
    .max(200)
    .refine((value) => {
    if (value === '')
        return true;
    try {
        new RegExp(value);
        return true;
    }
    catch {
        return false;
    }
}, { message: 'Muotosääntö ei ole kelvollinen säännöllinen lauseke.' })
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional();
export const attributeDefinitionCreateSchema = z.object({
    key: attributeKeySchema,
    label: attributeLabelSchema,
    target: attributeTargetSchema,
    pattern: attributePatternSchema,
    required: z.boolean().default(false),
    showOnReport: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(9999).default(0),
});
/**
 * `key` ei ole mukana: avaimen vaihtaminen olisi hiljainen rikkomus
 * jokaiselle tallennetulle vientilinkille ja jokaiselle skriptille, joka
 * lukee CSV:n sarakenimellä. Väärin nimetty avain poistetaan ja
 * perustetaan uudelleen.
 */
export const attributeDefinitionUpdateSchema = z
    .object({
    label: attributeLabelSchema,
    target: attributeTargetSchema,
    pattern: attributePatternSchema,
    required: z.boolean(),
    showOnReport: z.boolean(),
    sortOrder: z.number().int().min(0).max(9999),
    active: z.boolean(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Muutettavia kenttiä ei annettu.',
});
/**
 * Attribuuttien arvot asiakkaalle tai projektille.
 *
 * Kartta on **täsmennys, ei korvaus**: mukana olevat avaimet asetetaan,
 * mainitsemattomiin ei kosketa. Tyhjä arvo poistaa. Näin osittainen
 * PATCH — jonka lähettäjä ei tiedä kaikkia avaimia — ei voi tyhjentää
 * niitä, joista se ei tiennyt.
 */
export const attributeValuesSchema = z.record(attributeKeySchema, z
    .string()
    .trim()
    .max(MAX_ATTRIBUTE_VALUE_LENGTH, {
    message: `Arvo voi olla enintään ${MAX_ATTRIBUTE_VALUE_LENGTH} merkkiä.`,
}));
/**
 * Asiakkaan sävyvalinta (suunnitelma 10). `null` tyhjentää valinnan, jolloin
 * sävy johdetaan taas asiakaskoodista.
 *
 * Luettelo tulee paletista (`varit.ts`) eikä ole tässä toisteltuna: kaksi
 * kopiota eriytyisi, ja silloin kantaan pääsisi avain, jota mikään ei osaa
 * piirtää. Kannassa ei ole CHECK-rajoitetta samasta syystä toisin päin —
 * paletin muuttaminen ei saa vaatia migraatiota, ja tuntematon avain
 * putoaa piirrossa johdettuun (`savyAvain`).
 */
export const colorKeySchema = z.enum(SAVY_AVAIMET).nullable();
export const customerCreateSchema = z.object({
    code: codeSchema,
    name: nameSchema,
    defaultHourlyRate: optionalRateSchema,
    notes: notesSchema,
    colorKey: colorKeySchema.optional(),
    attributes: attributeValuesSchema.optional(),
    /**
     * Oletustehtävälajien siementäminen (suunnitelma 3). Oletuksena päällä:
     * asiakas ilman tehtävälajeja on asiakas, jolle ei voi kirjata tunteja,
     * ja se virhe huomataan vasta kun joku yrittää.
     */
    seedTaskTypes: z.boolean().default(true),
});
/**
 * Huomaa, ettei kuvaus (`notes`) ole tässä.
 *
 * Se on siirretty omaan päätteeseensä (`PATCH /api/customers/:id/description`),
 * koska sen kirjoitusoikeus on eri: kuvausta muokkaa myös asiakasvastaava,
 * hintoja ja koodeja ei. Kaksi eri oikeutta samassa päätteessä tarkoittaisi
 * kenttäkohtaista tarkistusta siellä, missä sitä ei osaa odottaa.
 */
export const customerUpdateSchema = z
    .object({
    code: codeSchema,
    name: nameSchema,
    defaultHourlyRate: optionalRateSchema,
    active: z.boolean(),
    colorKey: colorKeySchema,
    attributes: attributeValuesSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Muutettavia kenttiä ei annettu.',
});
/**
 * Huomautus tuntikirjauksesta (backlog 3.5).
 *
 * Teksti on pakollinen ja tyhjä torjutaan: huomautus lähtee sähköpostina
 * tekijälle, joka ei näkisi tyhjästä viestistä mitään. Yläraja on sama kuin
 * kirjauksen kuvauksella kaksinkertaisena — huomautus saa olla lause tai
 * kaksi, muttei keskustelu.
 */
export const entryNoteCreateSchema = z.object({
    text: z.string().trim().min(1, 'Huomautus ei voi olla tyhjä.').max(1000),
});
export const entryNoteResolveSchema = z.object({
    resolved: z.boolean(),
});
export const entryNoteQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    /** Oletuksena vain avoimet: kuitattu huomautus on hoidettu asia. */
    onlyOpen: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
});
export const taskTypeCreateSchema = z.object({
    customerId: uuidSchema,
    code: codeSchema,
    name: nameSchema,
    billable: z.boolean().default(true),
    hourlyRate: optionalRateSchema,
    sortOrder: z.number().int().min(0).max(9999).optional(),
    /**
     * Poissaolo (loma, sairaus) eikä työtä. Pienentää odotettua työaikaa
     * laskutusasteen laskennassa: lomaviikko ei tee kenestäkään
     * alisuoriutujaa. Sallittu vain sisäisellä asiakkaalla ja vain
     * laskuttamattomalle tehtävälajille — palvelin tarkistaa molemmat.
     */
    absence: z.boolean().default(false),
});
/**
 * Oletustehtävälajipohjan levitys jo perustetuille asiakkaille (backlog 2.11).
 *
 * Kopiointi asiakasta luotaessa on kertaluontoinen (suunnitelma 3), joten
 * pohjaan myöhemmin lisätty tehtävälaji ei koskaan päädy yhdellekään
 * olemassa olevalle asiakkaalle. Tämä on se pääte, joka sen tekee — ja
 * **vain lisää**: nimeä, hintaa tai laskutettavuutta se ei kosketa, koska
 * asiakkaan oma tehtävälaji voi olla tarkoituksella eri kuin pohjan.
 *
 * Asiakkaat annetaan aina nimeltä. "Kaikille" olisi yksi klikkaus, jonka
 * jälkeen kymmenien asiakkaiden tehtävälajilistat ovat muuttuneet eikä
 * kukaan tiedä mitkä.
 */
export const taskTypeTemplateApplySchema = z.object({
    customerIds: z.array(uuidSchema).min(1, 'Valitse vähintään yksi asiakas.').max(200),
});
export const taskTypeUpdateSchema = z
    .object({
    code: codeSchema,
    name: nameSchema,
    billable: z.boolean(),
    hourlyRate: optionalRateSchema,
    sortOrder: z.number().int().min(0).max(9999),
    active: z.boolean(),
    absence: z.boolean(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Muutettavia kenttiä ei annettu.',
});
const projectDatesRefinement = (value) => value.startsOn == null || value.endsOn == null || value.startsOn <= value.endsOn;
export const projectCreateSchema = z
    .object({
    customerId: uuidSchema,
    code: codeSchema,
    name: nameSchema,
    status: projectStatusSchema.default('ACTIVE'),
    startsOn: isoDateSchema.nullable().optional(),
    endsOn: isoDateSchema.nullable().optional(),
    hourlyRate: optionalRateSchema,
    budgetHours: optionalPositiveDecimalSchema,
    budgetValue: optionalPositiveDecimalSchema,
    workdayHours: optionalPositiveDecimalSchema,
    attributes: attributeValuesSchema.optional(),
})
    .refine(projectDatesRefinement, {
    message: 'Alkupäivä ei voi olla päättymispäivän jälkeen.',
    path: ['startsOn'],
});
export const projectUpdateSchema = z
    .object({
    code: codeSchema,
    name: nameSchema,
    status: projectStatusSchema,
    startsOn: isoDateSchema.nullable(),
    endsOn: isoDateSchema.nullable(),
    hourlyRate: optionalRateSchema,
    budgetHours: optionalPositiveDecimalSchema,
    budgetValue: optionalPositiveDecimalSchema,
    workdayHours: optionalPositiveDecimalSchema,
    attributes: attributeValuesSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Muutettavia kenttiä ei annettu.',
})
    .refine(projectDatesRefinement, {
    message: 'Alkupäivä ei voi olla päättymispäivän jälkeen.',
    path: ['startsOn'],
});
export const userUpdateSchema = z
    .object({
    role: roleSchema,
    active: z.boolean(),
    weeklyHours: optionalPositiveDecimalSchema,
    billableTargetShare: optionalBillableTargetShareSchema,
    /**
     * Esihenkilö (docs/KEHITYSSUUNNITELMAT.md, luku 5). `null` poistaa.
     *
     * Valinnainen kenttä jälkikäteen lisätylle, sama sääntö ja sama
     * peruste kuin koulutuksen esivaatimuksella (KOULUTUKSET 12):
     * puuttuva tarkoittaa "älä muuta". Oletusarvo `null` olisi se, jota ei
     * valita — silloin vanha lomake poistaisi esihenkilön hiljaa joka
     * kerta kun joku säätää viikkotunteja.
     */
    managerId: uuidSchema.nullable(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Muutettavia kenttiä ei annettu.',
});
// --- Paikalliset tilit -----------------------------------------------------
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email({ message: 'Anna kelvollinen sähköpostiosoite.' })
    .max(200);
/**
 * Salasanan pituusvaatimus ilman merkkiluokkasääntöjä.
 *
 * Pituus on ainoa vaatimus, joka mittaa arvattavuutta luotettavasti.
 * "Vähintään yksi erikoismerkki" tuottaa `Salasana1!`-tyyppisiä tunnuksia,
 * jotka ovat lyhyitä ja silti sääntöjen mukaisia. Yläraja on `scrypt`in
 * kustannuksen katto, ei laatuvaatimus.
 */
export const passwordSchema = z
    .string()
    .min(12, { message: 'Salasanassa on oltava vähintään 12 merkkiä.' })
    .max(200, { message: 'Salasana voi olla enintään 200 merkkiä.' });
export const localLoginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, { message: 'Anna salasana.' }),
});
/**
 * Paikallisen tilin luonti hallinnasta. Sähköpostin domainia ei rajata
 * `ALLOWED_EMAIL_DOMAINS`-listalla: paikallinen tili on olemassa juuri
 * niitä varten, jotka eivät ole tenantissa. Rajaus koskee Entra-provisiota,
 * ja se pysyy siellä.
 */
export const userCreateSchema = z.object({
    email: emailSchema,
    name: z.string().trim().min(1, { message: 'Nimi puuttuu.' }).max(200),
    role: roleSchema.default('USER'),
    weeklyHours: optionalPositiveDecimalSchema,
    billableTargetShare: optionalBillableTargetShareSchema,
    password: passwordSchema.optional(),
});
export const passwordSetSchema = z.object({
    password: passwordSchema,
    /** Pakottaa vaihdon ensimmäisellä kirjautumisella. */
    mustChange: z.boolean().default(true),
});
export const passwordChangeSchema = z.object({
    currentPassword: z.string().min(1, { message: 'Anna nykyinen salasana.' }),
    newPassword: passwordSchema,
});
// --- Kyselyparametrit ------------------------------------------------------
/** `"true"` / `"false"` merkkijonona — kyselyparametrit ovat aina tekstiä. */
const booleanQuery = z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional();
export const customerSearchSchema = z.object({
    q: z.string().trim().max(100).optional(),
    active: booleanQuery,
    /** `true` rajaa omiin vastuuasiakkaisiin. Suodatin, ei pakote (2.4). */
    mine: booleanQuery,
    includeInternal: booleanQuery,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const timeEntryQuerySchema = dateRangeSchema.and(z.object({
    userId: uuidSchema.optional(),
    customerId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
}));
export const projectQuerySchema = z.object({
    customerId: uuidSchema.optional(),
    status: projectStatusSchema.optional(),
    q: z.string().trim().max(100).optional(),
});
// --- Vienti ja lukitus -----------------------------------------------------
export const csvDialectSchema = z.enum(['excel-fi', 'standard']);
/** Kyselyparametri on aina tekstiä; oletus annetaan tekstinä ennen muunnosta. */
const booleanQueryWithDefault = (fallback) => z
    .enum(['true', 'false'])
    .default(fallback ? 'true' : 'false')
    .transform((value) => value === 'true');
/**
 * Viennin rajaus (suunnitelma 11).
 *
 * Sisäinen työ on oletuksena pois: lomat ja poissaolot eivät kuulu
 * laskutusaineistoon, ja mukana ne kasvattaisivat tuntisumman luvuksi,
 * jota ei voi laskuttaa keneltäkään.
 */
const exportQueryObject = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    userId: uuidSchema.optional(),
    customerId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    includeInternal: booleanQueryWithDefault(false),
    billableOnly: booleanQueryWithDefault(false),
    dialect: csvDialectSchema.default('excel-fi'),
    /**
     * CSV:n sarakkeet pilkulla eroteltuna. Puuttuva parametri = oletusjoukko
     * (`DEFAULT_EXPORT_COLUMNS`), jossa kuvaus ja attribuutit eivät ole
     * mukana.
     *
     * Tuntematon kiinteä sarake on virhe — kirjoitusvirhe sarakkeen nimessä
     * karsisi muuten sarakkeen hiljaisesti ja tiedosto näyttäisi oikealta.
     * Tuntematon `attr_`-sarake sen sijaan sallitaan ja pudotetaan vasta
     * ajossa: attribuutti on voitu poistaa sen jälkeen, kun linkki
     * tallennettiin kirjanmerkkeihin.
     */
    columns: z
        .string()
        .optional()
        .transform((value) => (value === undefined ? undefined : parseExportColumns(value)))
        .refine((value) => value === undefined || value.length > 0, {
        message: 'Valitse vähintään yksi sarake.',
    })
        .refine((value) => value === undefined ||
        value.every((column) => isExportColumnId(column) || column.startsWith(ATTRIBUTE_COLUMN_PREFIX)), { message: 'Tuntematon sarake.' }),
});
const validExportRange = (range) => range.from <= range.to;
const exportRangeError = {
    message: 'Alkupäivä ei voi olla loppupäivän jälkeen.',
    path: ['from'],
};
export const exportQuerySchema = exportQueryObject.refine(validExportRange, exportRangeError);
/**
 * Excel-vienti: sama rajaus ja sarakevalinta kuin CSV:llä, lisäksi
 * raportin ryhmittely ja otsikoiden kieli.
 *
 * Ryhmittely puuttuessaan = tasainen lista samassa järjestyksessä kuin
 * CSV. Kieli tulee kutsujalta, koska käyttöliittymän kieli elää vain
 * selaimessa (`kieli.ts`) eikä palvelin muuten tiedä sitä. `dialect`
 * hyväksytään mutta ei vaikuta mihinkään: Excel-tiedostossa luvut ovat
 * lukuja eikä erotinmerkkejä ole.
 */
export const xlsxExportQuerySchema = exportQueryObject
    .extend({
    groupBy: z.enum(['user', 'taskType', 'project']).optional(),
    lang: z.enum(APP_LANGUAGES).default(DEFAULT_LANGUAGE),
})
    .refine(validExportRange, exportRangeError);
export const monthLockQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
});
// --- Raportit --------------------------------------------------------------
export const reportSortSchema = z.enum(['value', 'hours', 'name']);
export const reportQuerySchema = z
    .object({
    from: isoDateSchema,
    to: isoDateSchema,
    includeInternal: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    sort: reportSortSchema.default('value'),
})
    .refine((range) => range.from <= range.to, {
    message: 'Alkupäivä ei voi olla loppupäivän jälkeen.',
    path: ['from'],
});
export const reportGroupBySchema = z.enum(['user', 'taskType', 'project']);
export const reportDetailQuerySchema = z
    .object({
    from: isoDateSchema,
    to: isoDateSchema,
    includeInternal: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    groupBy: reportGroupBySchema.default('user'),
})
    .refine((range) => range.from <= range.to, {
    message: 'Alkupäivä ei voi olla loppupäivän jälkeen.',
    path: ['from'],
});
export const checksQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
    month: z.coerce.number().int().min(1).max(12),
});
// --- Hinnoittelu ja muutosloki ---------------------------------------------
/**
 * Takautuva hintalaskenta (suunnitelma 5.4).
 *
 * Perustelu on pakollinen ja kuivaharjoitus oletus. Molemmat ovat
 * tarkoituksellisia: ajo kirjoittaa uusiksi jo kirjattujen tuntien arvon,
 * eli sen mitä asiakkaalta on laskutettu. Se saa olla mahdollista, mutta
 * ei vahingossa.
 */
export const rateRecalculateSchema = z
    .object({
    from: isoDateSchema,
    to: isoDateSchema,
    customerId: uuidSchema.nullable().optional(),
    projectId: uuidSchema.nullable().optional(),
    taskTypeId: uuidSchema.nullable().optional(),
    reason: z
        .string()
        .trim()
        .min(10, {
        message: 'Kerro lyhyesti miksi hinnat lasketaan uudelleen (vähintään 10 merkkiä).',
    })
        .max(500),
    dryRun: z.boolean().default(true),
})
    .refine((range) => range.from <= range.to, {
    message: 'Alkupäivä ei voi olla loppupäivän jälkeen.',
    path: ['from'],
});
export const auditLogQuerySchema = z.object({
    action: z.string().trim().max(50).optional(),
    targetType: z.string().trim().max(50).optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
// --- Järjestelmäasetukset --------------------------------------------------
/**
 * Valuutta otetaan vastaan vain tuettujen listalta. Vapaa koodikenttä
 * päästäisi kantaan merkkijonon, jota `Intl` ei tunne — ja silloin jokainen
 * euromäärä koko sovelluksessa kaatuisi muotoilussa, ei sillä sivulla
 * jossa arvo asetettiin.
 */
export const currencySchema = z.string().refine(isCurrencyCode, {
    message: `Valuutan on oltava yksi tuetuista: ${SUPPORTED_CURRENCIES.join(', ')}.`,
});
/**
 * Yrityksen nimi kirjautumissivulle ja tunnuksen vaihtoehtoiseksi tekstiksi.
 * Ei tyhjää: tyhjä nimi jättäisi ruudunlukijalle kuvan ilman selitystä.
 */
export const brandNameSchema = z
    .string()
    .trim()
    .min(1, { message: 'Yrityksen nimi ei voi olla tyhjä.' })
    .max(MAX_BRAND_NAME_LENGTH, {
    message: `Yrityksen nimi voi olla enintään ${MAX_BRAND_NAME_LENGTH} merkkiä.`,
});
/**
 * Muistutusten asetukset (backlog 3.1).
 *
 * Aikataulun laji on **kiinteä lajikohtaisesti** eikä valittavissa:
 * viikkomuistutus on viikkoa koskeva väite, eikä siitä tule kuukausittaista
 * sillä, että ajopäiväksi valitsee kuukauden päivän. Skeema tarkistaa, että
 * annettu aikataulu vastaa lajia — muuten hallintanäkymän virhe päätyisi
 * kantaan asti ja muistutus jäisi hiljaa lähtemättä.
 */
const reminderScheduleSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('WEEKLY'),
        weekday: z
            .number()
            .int()
            .min(1, { message: 'Viikonpäivä on 1–7 (maanantai–sunnuntai).' })
            .max(7, { message: 'Viikonpäivä on 1–7 (maanantai–sunnuntai).' }),
    }),
    z.object({
        kind: z.literal('MONTHLY'),
        dayFrom: z.number().int().min(1).max(MAX_REMINDER_DAY_OF_MONTH),
        dayTo: z.number().int().min(1).max(MAX_REMINDER_DAY_OF_MONTH),
    }),
    z.object({ kind: z.literal('DAILY') }),
]);
export const reminderConfigSchema = z
    .object({
    enabled: z.boolean(),
    schedule: reminderScheduleSchema,
    extraText: z
        .string()
        .trim()
        .max(1000, { message: 'Lisäteksti voi olla enintään 1000 merkkiä.' })
        .nullable()
        // Tyhjä kenttä on "ei lisäystä", ei tyhjä kappale viestin keskellä.
        .transform((value) => (value && value.length > 0 ? value : null)),
})
    .refine((value) => value.schedule.kind !== 'MONTHLY' || value.schedule.dayFrom <= value.schedule.dayTo, { message: 'Ajopäiväikkunan alku ei voi olla loppua myöhemmin.', path: ['schedule', 'dayTo'] });
export const remindersSettingSchema = z
    .object(Object.fromEntries(REMINDER_KINDS.map((kind) => [kind, reminderConfigSchema])))
    .refine((value) => REMINDER_KINDS.every((kind) => value[kind].schedule.kind === REMINDER_SCHEDULE_KIND[kind]), { message: 'Muistutuksen aikataulun laji ei vastaa muistutusta.' });
/**
 * Kehityskeskustelujen säilytysaika kuukausina. `0` = ei automaattista
 * poistoa (docs/KEHITYSSUUNNITELMAT.md, luku 11).
 *
 * Yläraja kymmenen vuotta: sitä pidempi säilytys ei ole säilytysaika vaan
 * ikuisuus kiertoilmauksena, ja silloin `0` on rehellisempi arvo.
 */
export const developmentRetentionMonthsSchema = z.number().int().min(0).max(120);
export const settingsUpdateSchema = z
    .object({
    currency: currencySchema,
    brandName: brandNameSchema,
    reminders: remindersSettingSchema,
    billableTargetShare: billableTargetShareSchema,
    developmentRetentionMonths: developmentRetentionMonthsSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Muutettavia asetuksia ei annettu.',
});
/** Yhden lajin esikatselu ja testilähetys hallintanäkymässä. */
export const reminderKindSchema = z.enum(REMINDER_KINDS);
export const reminderTestSchema = z.object({ kind: reminderKindSchema });
// --- Tuonti-API (suunnitelma 9) --------------------------------------------
/**
 * Lähdejärjestelmän tunniste. Tämä on koko tuonnin idempotenssin perusta
 * (9.2): rivi tunnistetaan tästä, ei sisällöstä. Puuttuva tunniste
 * hylätään — ilman sitä sama rivi loisi uuden kopion joka ajolla.
 */
export const externalIdSchema = z
    .string()
    .trim()
    .min(1, { message: 'Lähdejärjestelmän tunniste puuttuu.' })
    .max(120, { message: 'Tunniste voi olla enintään 120 merkkiä.' });
/** Viittaus toiseen entiteettiin lähdejärjestelmän tunnisteella (9.3). */
export const externalRefSchema = externalIdSchema;
export const importUserRowSchema = z.object({
    externalId: externalIdSchema,
    email: emailSchema,
    name: nameSchema,
    role: roleSchema.default('USER'),
    active: z.boolean().default(true),
    weeklyHours: optionalPositiveDecimalSchema,
});
export const importCustomerRowSchema = z.object({
    externalId: externalIdSchema,
    code: codeSchema,
    name: nameSchema,
    defaultHourlyRate: optionalRateSchema,
    isInternal: z.boolean().default(false),
    active: z.boolean().default(true),
    notes: notesSchema,
    /** Lisäattribuutit avain-arvokarttana. Tuntematon avain on rivivirhe. */
    attributes: attributeValuesSchema.optional(),
});
export const importTaskTypeRowSchema = z.object({
    externalId: externalIdSchema,
    customerExternalId: externalRefSchema,
    code: codeSchema,
    name: nameSchema,
    billable: z.boolean().default(true),
    hourlyRate: optionalRateSchema,
    sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
    active: z.boolean().default(true),
});
export const importProjectRowSchema = z.object({
    externalId: externalIdSchema,
    customerExternalId: externalRefSchema,
    code: codeSchema,
    name: nameSchema,
    hourlyRate: optionalRateSchema,
    budgetHours: optionalPositiveDecimalSchema,
    budgetValue: optionalPositiveDecimalSchema,
    workdayHours: optionalPositiveDecimalSchema,
    startsOn: isoDateSchema.nullable().optional(),
    endsOn: isoDateSchema.nullable().optional(),
    status: projectStatusSchema.default('ACTIVE'),
    /** Lisäattribuutit avain-arvokarttana. Tuntematon avain on rivivirhe. */
    attributes: attributeValuesSchema.optional(),
});
/**
 * Kirjauksen tuontirivi.
 *
 * `hourlyRate` on **merkkijono, ei JSON-luku** — suunnitelman esimerkissä
 * se oli luku, ja poikkeama on tietoinen. Sama sääntö on koko
 * järjestelmässä (`decimalStringSchema`): raha ei kulje liukulukuna, koska
 * JSON-luvun tarkkuus ei ole taattu. Tuonti on kaikkein huonoin paikka
 * poiketa siitä — se kirjoittaa historian laskutetuista tunneista.
 *
 * Hinta annetaan tässä eksplisiittisesti eikä periytymisketjusta (9.4):
 * historian pitää kertoa mitä on laskutettu, ei mitä nyt laskutettaisiin.
 */
export const importTimeEntryRowSchema = z.object({
    externalId: externalIdSchema,
    userExternalId: externalRefSchema,
    customerExternalId: externalRefSchema,
    taskTypeExternalId: externalRefSchema,
    projectExternalId: externalRefSchema.nullable().optional(),
    entryDate: isoDateSchema,
    minutes: minutesSchema,
    billable: z.boolean(),
    hourlyRate: optionalRateSchema,
    description: descriptionSchema,
});
/**
 * Erän kehys. `dryRun` validoi kirjoittamatta mitään ja palauttaa saman
 * raportin kuin oikea ajo (9.5).
 *
 * **Rivit otetaan vastaan jäsentämättöminä.** Se on tarkoituksellista: jos
 * rivit validoitaisiin tässä, ensimmäinen kelvoton rivi kaataisi koko
 * pyynnön yhteen Zod-virheeseen, eikä migraatioskripti saisi sitä
 * rivikohtaista raporttia, jonka takia koko rajapinta on olemassa. Rivit
 * jäsennetään yksi kerrallaan (`modules/import/engine.ts`), ja jokainen
 * hylätty rivi saa oman virheensä indeksillä ja lähdetunnisteella.
 *
 * `reason` on vapaaehtoinen mutta suositeltu: se päätyy muutoslokiin, ja
 * kolmannen ajon jälkeen "Historiatuonti 2024–2025, ajo 3" on ainoa asia,
 * joka erottaa rivit toisistaan.
 */
export const importEnvelopeSchema = z.object({
    dryRun: z.boolean().default(false),
    reason: z.string().trim().max(200).optional(),
    rows: z
        .array(z.unknown())
        .min(1, { message: 'Erä on tyhjä.' })
        .max(MAX_IMPORT_ROWS, {
        message: `Erässä voi olla enintään ${MAX_IMPORT_ROWS} riviä. Pilko suurempi erä.`,
    }),
});
// --- API-tunnukset ---------------------------------------------------------
/**
 * Päättymisaika on pakollinen, ei valinnainen (suunnitelma 9.5).
 * Ikuinen tunnus on tunnus, jota kukaan ei muista peruuttaa; oletus on
 * migraatioprojektin mittainen ja pisin sallittu vuosi.
 */
export const apiTokenCreateSchema = z.object({
    name: z.string().trim().min(1, { message: 'Anna tunnukselle nimi.' }).max(80),
    role: z.enum(API_TOKEN_ROLES).default('ADMIN'),
    expiresInDays: z.coerce.number().int().min(1).max(365).default(90),
});
/**
 * Oman agenttitunnuksen luonti (backlog 5).
 *
 * Erillinen skeema eikä `apiTokenCreateSchema`n muunnelma, koska rooli ei
 * ole tässä valittavissa: agenttitunnus toimii luojansa puolesta, joten
 * sen rooli on luojan rooli. Valittava rooli tarkoittaisi, että käyttäjä
 * voisi antaa koneelle enemmän kuin hänellä itsellään on.
 *
 * Voimassaolo on lyhyempi kuin migraatiotunnuksella samasta syystä kuin
 * päiväikkuna on olemassa: tunnus elää kehittäjän koneella agentin
 * ulottuvilla, eikä sellaisen pidä olla voimassa vuotta.
 */
export const agentTokenCreateSchema = z.object({
    name: z.string().trim().min(1, { message: 'Anna tunnukselle nimi.' }).max(80),
    expiresInDays: z.coerce.number().int().min(1).max(90).default(30),
});
// --- Kirjausehdotukset (backlog 5) -----------------------------------------
/**
 * Agentin ehdotus kirjaukseksi.
 *
 * Huomaa mitä tässä EI ole, ja miksi:
 *
 * - **`minutes`.** Agentti kertoo *kenelle ja mitä* — se näkee haaran,
 *   commitit ja repon. Ihminen sanoo *kauanko*, koska näppäimistöaika ei
 *   ole laskutettavaa aikaa: kone yliraportoisi koodaamisen ja
 *   aliraportoisi palaverit, puhelut, lukemisen ja ajattelun. Kesto ei ole
 *   tässä edes vihjeenä, koska vihje ankkuroi — ja ankkuroitu arvio on
 *   sama vinouma hitaammin.
 * - **`billable` ja `hourlyRate`.** Ne ratkaistaan palvelimella
 *   (`entryContext.ts`) vasta vahvistuksessa, samalla polulla kuin
 *   jokainen muukin kirjaus.
 *
 * `rationale` on pakollinen. Kuun lopussa on nähtävä, *miksi* kone ehdotti
 * juuri tätä; ilman perustelua ehdotus on arvaus, jonka alkuperää ei voi
 * enää tarkistaa.
 */
export const entrySuggestionCreateSchema = z.object({
    entryDate: isoDateSchema,
    customerId: uuidSchema,
    taskTypeId: uuidSchema,
    projectId: uuidSchema.nullable().optional(),
    description: descriptionSchema,
    /** Tehtävä, jota ehdotus koskee; kopioituu kirjaukseen vahvistuksessa. */
    kanbanTaskId: uuidSchema.nullable().optional(),
    rationale: z
        // `required_error` erikseen: zodin oletus puuttuvalle kentälle on
        // englanninkielinen "Required", ja tämä viesti menee agentin luettavaksi
        // — sen on kerrottava mitä puuttuu, ei mitä tyyppitarkistin ajatteli.
        .string({ required_error: 'Ehdotukselta puuttuu perustelu (`rationale`).' })
        .trim()
        .min(10, { message: 'Kerro lyhyesti mihin ehdotus perustuu (vähintään 10 merkkiä).' })
        .max(MAX_SUGGESTION_RATIONALE_LENGTH, {
        message: `Perustelu voi olla enintään ${MAX_SUGGESTION_RATIONALE_LENGTH} merkkiä.`,
    }),
});
/**
 * Ehdotuksen vahvistus kirjaukseksi.
 *
 * `minutes` on pakollinen: se on se osa, jota kone ei saanut sanoa, ja
 * vahvistus ilman sitä olisi kirjaus, jonka keston kukaan ei päättänyt.
 * Kuvauksen saa korjata samalla — ehdotettu teksti on koneen luonnos, ei
 * lopullinen.
 */
export const entrySuggestionConfirmSchema = z.object({
    minutes: minutesSchema,
    description: descriptionSchema,
    billable: z.boolean().optional(),
});
export const entrySuggestionQuerySchema = dateRangeSchema;
// --- Yrityksen tunnus ------------------------------------------------------
export const logoVariantSchema = z.string().refine(isLogoVariant, {
    message: 'Tunnuksen version on oltava vaalea tai tumma.',
});
/**
 * Tunnuksen lähetys base64-merkkijonona tavallisessa JSON-rungossa.
 *
 * Ei monipuolista lomakelähetystä (`multipart`): se toisi palvelimelle
 * riippuvuuden ja selaimeen oman polkunsa yhtä kenttää varten. Kuva on
 * enintään puoli megatavua, ja base64 kasvattaa sen kolmanneksella —
 * rungon raja on asetettu sen mukaan (`modules/branding/routes.ts`).
 *
 * Koko tarkistetaan **puretuista** tavuista eikä merkkijonon pituudesta:
 * merkkijonon pituus on se, mitä lähettäjä sattuu lähettämään, tavut se,
 * mitä kantaan päätyy.
 */
export const logoUploadSchema = z.object({
    fileName: z.string().trim().min(1).max(255),
    mimeType: z.string().refine(isLogoMimeType, {
        message: `Tunnuksen on oltava kuva: ${LOGO_MIME_TYPES.join(', ')}.`,
    }),
    dataBase64: z
        .string()
        .min(1, { message: 'Tiedosto puuttuu.' })
        .refine((value) => base64ByteLength(value) <= LOGO_MAX_BYTES, {
        message: `Tunnus voi olla enintään ${Math.round(LOGO_MAX_BYTES / 1024)} kt.`,
    }),
});
/** Base64-merkkijonon tavumäärä ilman purkamista. */
function base64ByteLength(value) {
    const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0;
    return Math.floor((value.length * 3) / 4) - padding;
}
// --- Koulutukset (docs/KOULUTUKSET.md) -------------------------------------
export const trainingCodeSchema = z.string().trim().regex(TRAINING_CODE_PATTERN, {
    message: 'Koulutuksen koodi on isoja kirjaimia, numeroita ja alaviivoja, esim. GDPR.',
});
export const trainingKeySchema = z.string().trim().regex(TRAINING_KEY_PATTERN, {
    message: 'Avain on pieniä kirjaimia, numeroita ja väliviivoja, esim. 01-mika-on-gdpr.',
});
/**
 * Koulutuksen asetukset hallintanäkymästä.
 *
 * Huomaa mitä tässä EI ole: `name`, `summary`, osiot eikä kysymykset. Ne
 * tulevat koodista ja kirjoittuvat kantaan synkronoinnissa
 * (`koulutukset:sync`). Jos ylläpitäjä voisi muokata niitä selaimessa,
 * seuraava synkronointi kumoaisi muutoksen hiljaa — ja pahempi vaihtoehto
 * olisi, ettei kumoaisi, jolloin kannassa oleva tentti eroaisi siitä, joka
 * on katselmoitu ja hyväksytty gitissä.
 *
 * Täällä ovat siis vain ne asiat, jotka ovat **talon päätöksiä eivätkä
 * sisältöä**: kenelle, kuinka tiukasti ja kuinka pitkäksi aikaa.
 */
export const trainingSettingsSchema = z.object({
    status: z.enum(TRAINING_STATUSES),
    passScorePercent: z.coerce
        .number()
        .int()
        .min(MIN_PASS_SCORE_PERCENT, {
        message: `Läpäisyraja ei voi olla alle ${MIN_PASS_SCORE_PERCENT} %.`,
    })
        .max(MAX_PASS_SCORE_PERCENT),
    questionCount: z.coerce.number().int().min(MIN_EXAM_QUESTION_COUNT).max(MAX_EXAM_QUESTION_COUNT),
    /** `null` = ei aikarajaa. Yhden session sääntö on silti voimassa. */
    timeLimitMinutes: z.coerce
        .number()
        .int()
        .min(MIN_EXAM_TIME_LIMIT_MINUTES)
        .max(MAX_EXAM_TIME_LIMIT_MINUTES)
        .nullable(),
    sessionMaxMinutes: z.coerce
        .number()
        .int()
        .min(MIN_EXAM_SESSION_MINUTES)
        .max(MAX_EXAM_SESSION_MINUTES),
    retryCooldownMinutes: z.coerce.number().int().min(0).max(MAX_RETRY_COOLDOWN_MINUTES),
    /** `null` = suoritus ei vanhene. */
    validityMonths: z.coerce
        .number()
        .int()
        .min(MIN_TRAINING_VALIDITY_MONTHS)
        .max(MAX_TRAINING_VALIDITY_MONTHS)
        .nullable(),
    expiryWarningDays: z.coerce.number().int().min(0).max(MAX_EXPIRY_WARNING_DAYS),
    requireMaterialBeforeExam: z.boolean(),
    /**
     * Esivaatimus: koulutus, jonka on oltava suoritettuna ja voimassa ennen
     * kuin tämän tentin voi aloittaa. `null` = ei esivaatimusta.
     *
     * **Valinnainen, ja se on ainoa valinnainen kenttä tässä muuten
     * kokonaisuutena lähetettävässä lomakkeessa.** Kenttä lisättiin
     * jälkikäteen, ja jälkikäteen lisätylle on kaksi vaihtoehtoa: pakollinen,
     * jolloin vanha selaimen nippu saa jokaisesta tallennuksesta 400:n — tai
     * valinnainen, jolloin se tallentaa kuten ennenkin eikä koske
     * esivaatimukseen. Kolmas vaihtoehto, `default(null)`, on se jota ei
     * valita: silloin vanha lomake **poistaisi** esivaatimuksen hiljaa joka
     * kerta kun joku säätää läpäisyrajaa.
     *
     * Puuttuva kenttä tarkoittaa siis "älä muuta": `undefined` ei päädy
     * Prisman `update`en eikä muutoslokiin (`changedFields`).
     *
     * Skeema tarkistaa vain muodon. Se, ettei koulutus ole oma
     * esivaatimuksensa eikä ketju sulkeudu kehäksi, ratkaistaan päätteessä ja
     * kannan liipaisimessa: kumpikin tarvitsee tietoa, jota tässä ei ole.
     */
    prerequisiteTrainingId: uuidSchema.nullable().optional(),
});
/**
 * Yksi kohdennus: rooli tai henkilö, pakollisena tai tarjolla.
 *
 * Rooli ja käyttäjä ovat toisensa poissulkevat, ja se pakotetaan sekä
 * täällä että kannan CHECK-rajoitteella. Kaksi tarkistusta samalle
 * säännölle ei ole tuplausta: tämä kertoo lomakkeelle mikä meni pieleen,
 * kanta pitää huolen siitä, ettei sääntö riipu siitä mikä pääte rivin
 * kirjoitti.
 */
export const trainingAssignmentSchema = z
    .discriminatedUnion('kind', [
    z.object({
        kind: z.literal('ROLE'),
        role: roleSchema,
        mandatory: z.boolean().default(true),
    }),
    z.object({
        kind: z.literal('USER'),
        userId: uuidSchema,
        mandatory: z.boolean().default(true),
    }),
])
    .describe('Koulutuksen kohdennus roolille tai yksittäiselle käyttäjälle.');
/**
 * Kohdennukset kokonaisuutena, ei lisäyksinä ja poistoina.
 *
 * Lomake näyttää koko joukon, joten se myös lähettää koko joukon. Erilliset
 * lisää/poista-päätteet tarkoittaisivat, että selain ja palvelin voivat olla
 * eri mieltä siitä, mitkä kohdennukset ovat voimassa — ja "kuka on
 * velvollinen käymään tämän koulutuksen" on huonoin mahdollinen kysymys
 * ollakseen kahta mieltä.
 */
export const trainingAssignmentsSchema = z.object({
    assignments: z.array(trainingAssignmentSchema).max(200),
});
/**
 * Yhden monivalinnan vastaus. Palvelin ei kerro, oliko se oikein.
 *
 * Aina joukko, myös yhden oikean vastauksen kysymyksessä: pääte on sama
 * molemmille, ja "montako valintaa tähän kysymykseen kuuluu" on
 * kysymyspaperin tieto, ei pyynnön muodon.
 *
 * **Tyhjä joukko on sallittu ja tarkoittaa vastauksen poistoa.** Käyttäjä,
 * joka poistaa viimeisenkin rastin, on jättänyt vastaamatta — vaihtoehto
 * olisi jättää kanta väittämään, että hän vastasi tyhjää.
 */
export const examAnswerSchema = z.object({
    choiceKeys: z
        .array(trainingKeySchema)
        .max(EXAM_CHOICE_COUNT)
        .refine((keys) => new Set(keys).size === keys.length, {
        message: 'Sama vaihtoehto on valittu kahdesti.',
    }),
});
/**
 * Koulutusraportin rajaus.
 *
 * Oletuksena vain aktiiviset käyttäjät: passivoitu tili ei ole
 * koulutusvelvollinen, ja lähteneiden rivit tekisivät matriisista
 * lukukelvottoman jo ensimmäisen vuoden jälkeen. Historia on silti
 * tallessa — `includeInactive` näyttää sen, kun joku kysyy mitä
 * viime vuonna oli.
 */
export const trainingReportQuerySchema = z.object({
    trainingId: uuidSchema.optional(),
    includeInactive: z.coerce.boolean().default(false),
    onlyProblems: z.coerce.boolean().default(false),
});
/**
 * Ylläpitäjän käsin kirjaama suoritus.
 *
 * Tarvitaan, koska koulutus voi olla käyty muualla: ulkopuolinen kurssi,
 * edellisen työnantajan todistus, konsultin pitämä sisäinen tilaisuus.
 * Ilman tätä compliance-raportti näyttäisi puutteen siellä, missä
 * puutetta ei ole — ja se opettaisi lukemaan raporttia väärin.
 *
 * `note` on **pakollinen**: käsin kirjattu suoritus ilman perustelua on
 * merkintä, jonka alkuperää ei voi jälkikäteen tarkistaa, ja se on juuri
 * se, mitä auditoinnissa kysytään.
 */
export const trainingCompletionRecordSchema = z.object({
    userId: uuidSchema,
    completedOn: isoDateSchema,
    note: z
        .string()
        .trim()
        .min(5, { message: 'Kerro lyhyesti, mihin suoritus perustuu (vähintään 5 merkkiä).' })
        .max(500),
    /** Pisteet, jos suoritus tuli mitattavasta tentistä muualla. */
    scorePercent: z.coerce.number().int().min(0).max(100).nullable().optional(),
});
// --- Projektinhallinta (docs/PROJEKTINHALLINTA.md) --------------------------
export const kanbanColumnSchema = z.enum(KANBAN_COLUMNS);
export const taskEstimateSchema = z.enum(TASK_ESTIMATES);
export const kanbanDueCauseSchema = z.enum(KANBAN_DUE_CAUSES);
const laneNameSchema = z
    .string()
    .trim()
    .min(1, { message: 'Anna uimaradalle nimi.' })
    .max(KANBAN_LANE_NAME_MAX, {
    message: `Nimi voi olla enintään ${KANBAN_LANE_NAME_MAX} merkkiä.`,
});
export const kanbanLaneCreateSchema = z.object({
    customerId: uuidSchema,
    /** `null` = asiakkuuden taulu, muuten asiakkaan projektin taulu. */
    projectId: uuidSchema.nullable().optional(),
    name: laneNameSchema,
});
/** Taulun avaaminen: viimeksi käytettyjen listaa varten (luku 13). */
export const boardVisitSchema = z.object({
    customerId: uuidSchema,
    /** `null` = asiakkuuden taulu, muuten asiakkaan projektin taulu. */
    projectId: uuidSchema.nullable().optional(),
});
/** Taulun notifikaatioasetukset (luku 15). Kaikki kolme annetaan aina. */
export const boardSettingsSchema = z.object({
    customerId: uuidSchema,
    projectId: uuidSchema.nullable().optional(),
    blockedReminder: z.boolean(),
    notifyBlocked: z.boolean(),
    notifyAssigned: z.boolean(),
    notifyMention: z.boolean(),
    milestoneReminder: z.boolean(),
});
/** Relaation lisäys: kohde ja laji; lähde on polun tehtävä (luku 6). */
export const taskRelationCreateSchema = z.object({
    taskId: uuidSchema,
    kind: z.enum(KANBAN_RELATION_KINDS),
});
/** Tehtävähaku relaation lisäykseen: asiakkuuden taulut, otsikon osa. */
export const taskSearchSchema = z.object({
    customerId: uuidSchema,
    q: z.string().trim().min(1).max(100),
});
/** Henkilötyöpäivät puolikkaan tarkkuudella: 0, 0.5, 1, … (luku 5). */
export const estimateDaysSchema = decimalStringSchema
    .refine((value) => Number(value) >= 0 && Number(value) <= 9999, {
    message: 'Arvion on oltava 0–9999 henkilötyöpäivää.',
})
    .refine((value) => Number.isInteger(Number(value) * 2), {
    message: 'Arvio annetaan puolikkaan henkilötyöpäivän tarkkuudella.',
});
export const laneStatusSchema = z.enum(LANE_STATUSES);
export const kanbanLaneUpdateSchema = z
    .object({
    name: laneNameSchema,
    /** Uusi sijainti taulussa, 0 = ensimmäinen. Palvelin rajaa loppuun. */
    position: z.number().int().min(0),
    // Sopimuksen puoli (luku 5): moderaattorin kenttiä.
    estimateDays: estimateDaysSchema.nullable(),
    startsOn: isoDateSchema.nullable(),
    endsOn: isoDateSchema.nullable(),
    status: laneStatusSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' })
    .refine((value) => !value.startsOn || !value.endsOn || value.startsOn <= value.endsOn, {
    message: 'Valmistumispäivä ei voi olla ennen aloituspäivää.',
    path: ['endsOn'],
});
/**
 * Vastuuhenkilö yhtenä kenttänä: jäsen, AI-assistentti, asiakas, kumppani
 * tai ei kukaan. Yksi kenttä, jonka tila ei voi olla ristiriitainen — kaksi
 * erillistä kenttää (`userId`, `ai`) voisivat olla molemmat asetettuja
 * (luku 6). Asiakkaan ja kumppanin henkilö on valinnainen (luku 29).
 */
export const taskAssigneeSchema = z
    .discriminatedUnion('kind', [
    z.object({ kind: z.literal('USER'), userId: uuidSchema }),
    z.object({ kind: z.literal('AI') }),
    z.object({ kind: z.literal('CUSTOMER'), contactId: uuidSchema.nullable().default(null) }),
    z.object({
        kind: z.literal('PARTNER'),
        partnerId: uuidSchema,
        contactId: uuidSchema.nullable().default(null),
    }),
])
    .nullable();
const taskTitleSchema = z
    .string()
    .trim()
    .min(1, { message: 'Anna tehtävälle otsikko.' })
    .max(KANBAN_TASK_TITLE_MAX, {
    message: `Otsikko voi olla enintään ${KANBAN_TASK_TITLE_MAX} merkkiä.`,
});
const taskDescriptionSchema = z
    .string()
    .trim()
    .max(KANBAN_TASK_DESCRIPTION_MAX, {
    message: `Kuvaus voi olla enintään ${KANBAN_TASK_DESCRIPTION_MAX} merkkiä.`,
})
    .transform((value) => (value.length === 0 ? null : value))
    .nullable();
export const kanbanTaskCreateSchema = z.object({
    laneId: uuidSchema,
    column: kanbanColumnSchema.default('TODO'),
    title: taskTitleSchema,
    description: taskDescriptionSchema.optional(),
    estimate: taskEstimateSchema.nullable().optional(),
    assignee: taskAssigneeSchema.optional(),
    /** Saman uimaradan milestone (luku 26). */
    milestoneId: uuidSchema.nullable().optional(),
    /** Deadline (luku 28); vain moderaattori. */
    dueOn: isoDateSchema.nullable().optional(),
});
export const kanbanTaskUpdateSchema = z
    .object({
    title: taskTitleSchema,
    description: taskDescriptionSchema,
    estimate: taskEstimateSchema.nullable(),
    assignee: taskAssigneeSchema,
    /** Deadline (luku 28). Kentän muutos vaatii moderaattorin, muut omistajan. */
    dueOn: isoDateSchema.nullable(),
    /**
     * Deadlinen siirron syy (luku 29). Huomioidaan vain, kun `dueOn`
     * muuttuu jo asetetusta päivästä; ensimmäisellä asetuksella ei syytä.
     */
    dueCause: kanbanDueCauseSchema.nullable(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' });
/** Siirto: kohdeuimarata, osio ja sijainti osiossa (0 = ensimmäinen). */
export const kanbanTaskMoveSchema = z
    .object({
    laneId: uuidSchema,
    column: kanbanColumnSchema,
    position: z.number().int().min(0),
    /** Perustelu kommenttina; pakollinen SKIPPED-osioon (luku 5). */
    comment: z
        .string()
        .trim()
        .min(1, { message: 'Kirjoita perustelu.' })
        .max(KANBAN_COMMENT_MAX, {
        message: `Perustelu voi olla enintään ${KANBAN_COMMENT_MAX} merkkiä.`,
    })
        .optional(),
})
    .refine((value) => value.column !== 'SKIPPED' || value.comment, {
    message: 'SKIPPED-osioon siirto vaatii perustelun: miksi tehtävää ei toteuteta.',
    path: ['comment'],
});
/**
 * Monistus. Kaikki valinnaisia: ilman kohdetta kopio syntyy alkuperäisen
 * perään samaan osioon — kuten kalenterin monistuspainike (backlog 9).
 */
export const kanbanTaskCopySchema = z.object({
    laneId: uuidSchema.optional(),
    column: kanbanColumnSchema.optional(),
    position: z.number().int().min(0).optional(),
});
// --- Milestonet (luku 26) ----------------------------------------------------
const milestoneNameSchema = z
    .string()
    .trim()
    .min(1, { message: 'Anna milestonelle nimi.' })
    .max(KANBAN_MILESTONE_NAME_MAX, {
    message: `Nimi voi olla enintään ${KANBAN_MILESTONE_NAME_MAX} merkkiä.`,
});
const milestoneDescriptionSchema = z
    .string()
    .trim()
    .max(KANBAN_MILESTONE_DESCRIPTION_MAX, {
    message: `Kuvaus voi olla enintään ${KANBAN_MILESTONE_DESCRIPTION_MAX} merkkiä.`,
})
    .transform((value) => (value.length === 0 ? null : value))
    .nullable();
/** Uusi milestone uimaradalle; uimarata on polussa. */
export const kanbanMilestoneCreateSchema = z.object({
    name: milestoneNameSchema,
    description: milestoneDescriptionSchema.optional(),
    dueOn: isoDateSchema.nullable().optional(),
});
/** Muokkaus; `reached` merkitsee saavutetuksi tai purkaa merkinnän. */
export const kanbanMilestoneUpdateSchema = z
    .object({
    name: milestoneNameSchema,
    description: milestoneDescriptionSchema,
    dueOn: isoDateSchema.nullable(),
    reached: z.boolean(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' });
/**
 * Asiakasraportti (PROJEKTINHALLINTA 27). `scope`: BOARD = vain tämä
 * taulu, CUSTOMER = asiakkuuden taulu ja sen projektien taulut. Tunnit
 * ovat oletuksena pois, koska raportti lähtee asiakkaalle ja tuntien
 * näyttäminen on sopimuskohtainen päätös.
 */
export const customerReportQuerySchema = z.object({
    customerId: uuidSchema,
    projectId: uuidSchema.nullable().optional(),
    scope: z.enum(['BOARD', 'CUSTOMER']).default('BOARD'),
    completedDays: z.coerce
        .number()
        .int()
        .refine((value) => CUSTOMER_REPORT_PERIODS.includes(value), {
        message: `Jakso on jokin näistä: ${CUSTOMER_REPORT_PERIODS.join(', ')} päivää.`,
    })
        .default(14),
    hours: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    lang: z.enum(APP_LANGUAGES).default(DEFAULT_LANGUAGE),
});
/** Tehtävän liitos milestoneen; `null` irrottaa (luku 26). */
export const taskMilestoneSchema = z.object({
    milestoneId: uuidSchema.nullable(),
});
/** Tehtävän kommentti (luku 6): pelkkää tekstiä, ei tyhjä. */
export const kanbanCommentSchema = z.object({
    body: z
        .string()
        .trim()
        .min(1, { message: 'Kirjoita kommentti.' })
        .max(KANBAN_COMMENT_MAX, {
        message: `Kommentti voi olla enintään ${KANBAN_COMMENT_MAX} merkkiä.`,
    }),
});
/** Reaktion emoji polun parametrina (PROJEKTINHALLINTA 22). */
export const reactionEmojiSchema = z.enum(REACTION_EMOJIS);
// --- Tehtävämuistutus (PROJEKTINHALLINTA 24) ---------------------------------
export const timeOfDaySchema = z
    .string()
    .refine(isTimeOfDay, { message: 'Kellonajan on oltava muodossa TT:MM.' });
const reminderNoteSchema = z
    .string()
    .trim()
    .max(KANBAN_REMINDER_NOTE_MAX, {
    message: `Saate voi olla enintään ${KANBAN_REMINDER_NOTE_MAX} merkkiä.`,
})
    .nullable();
/**
 * Muistutuksen asetus: **päivä ja kellonaika Suomen aikaa**, ei valmis
 * aikaleima.
 *
 * Selain voisi laskea hetken itse, mutta silloin kaksi kerrosta joutuisi
 * olemaan samaa mieltä aikavyöhykkeestä — ja väärä sopimus näkyisi vasta
 * kellonsiirron jälkeen, kun muistutus tulee tuntia myöhässä. Nyt muunnos
 * on yhdessä paikassa (`appZoneInstant`).
 *
 * Esivalinta (`preset`) on vaihtoehto päivälle: sen päivä lasketaan
 * palvelimella (`taskReminderPresetDate`), jotta "neljän viikon päästä"
 * tarkoittaa samaa riippumatta siitä, mikä päivä selaimen kello on.
 */
export const taskReminderCreateSchema = z
    .object({
    date: isoDateSchema.optional(),
    preset: z.enum(KANBAN_REMINDER_PRESETS).optional(),
    time: timeOfDaySchema,
    audience: z.enum(KANBAN_REMINDER_AUDIENCES).default('SETTER'),
    note: reminderNoteSchema.optional(),
})
    .refine((value) => Boolean(value.date) !== Boolean(value.preset), {
    message: 'Anna joko päivämäärä tai esivalinta.',
    path: ['date'],
});
/** Siirto toiseen aikaan, kohdejoukon tai saatteen muutos. */
export const taskReminderUpdateSchema = z
    .object({
    date: isoDateSchema,
    time: timeOfDaySchema,
    audience: z.enum(KANBAN_REMINDER_AUDIENCES),
    note: reminderNoteSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' })
    .refine((value) => Boolean(value.date) === Boolean(value.time), {
    message: 'Päivä ja kellonaika muutetaan yhdessä.',
    path: ['time'],
});
// --- Asiakkaan yhteyshenkilöt ja dokumenttilinkit --------------------------
//
// Nämä eivät ole lisäattribuutteja, vaikka nekin ovat asiakkaan tietoa.
// Attribuutti on yksi arvo per määritelty avain; yhteyshenkilöitä ja
// linkkejä on 0..n ja niillä on oma sisäinen rakenteensa.
/** Yhteyshenkilön nimi. Lyhyempi kuin asiakkaan nimi: ihminen, ei yritys. */
export const contactNameSchema = z
    .string()
    .trim()
    .min(2, { message: 'Nimessä on oltava vähintään 2 merkkiä.' })
    .max(120, { message: 'Nimi voi olla enintään 120 merkkiä.' });
/**
 * Tyhjä merkkijono on tyhjennys, ei virhe.
 *
 * Käyttöliittymässä kentän tyhjentäminen on tapa poistaa arvo, ja lomake
 * lähettää tyhjän kentän tyhjänä merkkijonona. Ilman tätä muunnosta
 * puhelinnumeron poistaminen olisi validointivirhe.
 */
const tyhjaNulliksi = (skeema) => z.preprocess((arvo) => (typeof arvo === 'string' && arvo.trim() === '' ? null : arvo), skeema);
export const contactRoleSchema = tyhjaNulliksi(z.string().trim().max(120, { message: 'Rooli voi olla enintään 120 merkkiä.' }).nullable());
export const contactEmailSchema = tyhjaNulliksi(emailSchema.nullable());
export const contactPhoneSchema = tyhjaNulliksi(z.string().trim().max(40, { message: 'Puhelinnumero voi olla enintään 40 merkkiä.' }).nullable());
export const contactNotesSchema = tyhjaNulliksi(z.string().trim().max(500, { message: 'Huomio voi olla enintään 500 merkkiä.' }).nullable());
/** Kuvauksen oma pääte: eri oikeus kuin muulla perustiedolla. */
export const customerDescriptionSchema = z.object({ notes: notesSchema });
export const customerContactCreateSchema = z.object({
    name: contactNameSchema,
    role: contactRoleSchema.optional(),
    email: contactEmailSchema.optional(),
    phone: contactPhoneSchema.optional(),
    notes: contactNotesSchema.optional(),
    isPrimary: z.boolean().default(false),
});
export const customerContactUpdateSchema = z
    .object({
    name: contactNameSchema,
    role: contactRoleSchema,
    email: contactEmailSchema,
    phone: contactPhoneSchema,
    notes: contactNotesSchema,
    isPrimary: z.boolean(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' });
// --- Kumppanit (PROJEKTINHALLINTA 29) -------------------------------------------
const partnerNameSchema = z
    .string()
    .trim()
    .min(2, { message: 'Kumppanin nimessä on oltava vähintään 2 merkkiä.' })
    .max(PARTNER_NAME_MAX, { message: `Nimi voi olla enintään ${PARTNER_NAME_MAX} merkkiä.` });
const partnerDescriptionSchema = tyhjaNulliksi(z
    .string()
    .trim()
    .max(PARTNER_DESCRIPTION_MAX, {
    message: `Kuvaus voi olla enintään ${PARTNER_DESCRIPTION_MAX} merkkiä.`,
}));
export const partnerCreateSchema = z.object({
    name: partnerNameSchema,
    description: partnerDescriptionSchema.optional(),
});
export const partnerUpdateSchema = z
    .object({
    name: partnerNameSchema,
    description: partnerDescriptionSchema,
    /** Passivointi piilottaa valinnoista mutta säilyttää historian. */
    active: z.boolean(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' });
/** Kumppanin yhteyshenkilö: samat kentät kuin asiakkaan, ilman ensisijaisuutta. */
export const partnerContactCreateSchema = customerContactCreateSchema.omit({ isPrimary: true });
export const partnerContactUpdateSchema = z
    .object({
    name: contactNameSchema,
    role: contactRoleSchema,
    email: contactEmailSchema,
    phone: contactPhoneSchema,
    notes: contactNotesSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' });
export const linkLabelSchema = z
    .string()
    .trim()
    .min(2, { message: 'Nimilapussa on oltava vähintään 2 merkkiä.' })
    .max(120, { message: 'Nimilappu voi olla enintään 120 merkkiä.' });
/**
 * Osoite validoidaan samalla säännöllä kuin se renderöidään: vain http ja
 * https. Perustelu on `asiakastiedot.ts`:ssä.
 */
export const linkUrlSchema = z
    .string()
    .trim()
    .max(2000, { message: 'Osoite voi olla enintään 2000 merkkiä.' })
    .refine(onSallittuDokumenttiUrl, {
    message: 'Anna kelvollinen http- tai https-osoite.',
});
/**
 * Tagit normalisoidaan ennen rajojen tarkistusta, jotta ”Sopimus” ja
 * ”sopimus” eivät vie kahta paikkaa kahdeksasta.
 */
export const linkTagsSchema = z
    .array(z.string().max(100))
    .max(50, { message: 'Liian monta tagia.' })
    .transform(normalisoiTagit)
    .refine((tagit) => tagit.length <= TAGEJA_ENINTAAN, {
    message: `Tageja voi olla enintään ${TAGEJA_ENINTAAN}.`,
});
export const customerLinkCreateSchema = z.object({
    label: linkLabelSchema,
    url: linkUrlSchema,
    tags: linkTagsSchema.default([]),
});
export const customerLinkUpdateSchema = z
    .object({
    label: linkLabelSchema,
    url: linkUrlSchema,
    tags: linkTagsSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' });
/**
 * Järjestyksen muutos koko listana eikä rivi kerrallaan.
 *
 * Kaksi peräkkäistä ”siirrä ylös” -painallusta tuottaisi rivikohtaisella
 * päätteellä kaksi kilpailevaa kirjoitusta samaan joukkoon. Koko lista
 * kerralla on yksi kirjoitus, jonka lopputulos on se, mitä käyttäjä näkee.
 */
export const sortOrderSchema = z.object({
    ids: z.array(uuidSchema).min(1, { message: 'Anna järjestettävät rivit.' }),
});
// --- Osaamisrekisteri (docs/KEHITYSSUUNNITELMAT.md, luku 6) -----------------
/**
 * Sertifikaattikoodi on tunniste, ei nimi. Isot kirjaimet, numerot ja
 * väliviiva — `AZ-104`, `SAA-C03`, `CKA`, `1Z0-1085`.
 *
 * Muoto pakotetaan, koska rekisterin koko arvo on kysymyksessä "montako
 * AZ-104:ää talossa on": vapaa muoto tuottaisi `az-104`:n ja
 * `AZ 104`:n omiksi riveikseen, ja vastaus olisi kolme kertaa väärä.
 * Syöte siistitään isoiksi kirjaimiksi ennen tarkistusta, jotta pienellä
 * kirjoittaminen on mukavuus eikä virhe.
 */
export const certificationCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2, { message: 'Koodissa on oltava vähintään 2 merkkiä.' })
    .max(SERTIFIKAATTI_KOODI_MAX, {
    message: `Koodi voi olla enintään ${SERTIFIKAATTI_KOODI_MAX} merkkiä.`,
})
    .regex(/^[A-Z0-9][A-Z0-9-]*$/, {
    message: 'Koodissa voi olla vain kirjaimia, numeroita ja väliviivoja.',
});
export const certificationNameSchema = z
    .string()
    .trim()
    .min(2, { message: 'Nimessä on oltava vähintään 2 merkkiä.' })
    .max(SERTIFIKAATTI_NIMI_MAX);
export const certificationProviderSchema = z
    .string()
    .trim()
    .min(2, { message: 'Anna tarjoaja.' })
    .max(SERTIFIKAATTI_TARJOAJA_MAX);
/**
 * Voimassaolo kuukausina. `null` = ei vanhene.
 *
 * Yläraja on sama kuin koulutuksilla (120 kk): sitä pidempi voimassaolo on
 * käytännössä "ei vanhene", ja se valinta on parempi tehdä `null`illa kuin
 * luvulla, jonka merkitystä joutuu arvaamaan.
 */
export const certificationValidityMonthsSchema = z
    .number()
    .int()
    .min(MIN_TRAINING_VALIDITY_MONTHS)
    .max(MAX_TRAINING_VALIDITY_MONTHS)
    .nullable();
/** Sama sääntö kuin dokumenttilinkillä: vain http ja https, ja se renderöidään. */
export const certificationUrlSchema = tyhjaNulliksi(z
    .string()
    .trim()
    .max(500)
    .refine(onSallittuDokumenttiUrl, { message: 'Anna kelvollinen http- tai https-osoite.' })
    .nullable());
export const certificationCreateSchema = z.object({
    code: certificationCodeSchema,
    name: certificationNameSchema,
    provider: certificationProviderSchema,
    validityMonths: certificationValidityMonthsSchema.default(null),
    url: certificationUrlSchema.optional(),
});
/**
 * Koodi ei ole muutettavissa.
 *
 * Se on rivin tunniste myös ihmiselle, ja sen vaihtaminen tekisi
 * historiallisesta raportista toisen sertifikaatin raportin ilman, että
 * yksikään saavutusrivi muuttuisi. Väärä koodi korjataan passivoimalla
 * rivi ja luomalla oikea.
 */
export const certificationUpdateSchema = z
    .object({
    name: certificationNameSchema,
    provider: certificationProviderSchema,
    validityMonths: certificationValidityMonthsSchema,
    url: certificationUrlSchema,
    active: z.boolean(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' });
/**
 * Saavutuksen kirjaus.
 *
 * `expiresOn` on **kolmiarvoinen**, ja se on tarkoitus:
 *   - puuttuva  = laske luettelon voimassaolosta (`oletusVanhenemisPaiva`)
 *   - `null`    = ei vanhene, vaikka luettelo sanoisi muuta
 *   - päivä     = todistuksessa lukeva päivä
 *
 * Kaksiarvoisena oletuksen ohittaminen "ei vanhene" -suuntaan olisi
 * mahdotonta ilman toista kenttää, ja juuri se tapaus on todellinen:
 * tarjoaja on lopettanut sertifikaatin vanhenemisen kesken kauden.
 */
export const userCertificationCreateSchema = z.object({
    certificationId: uuidSchema,
    achievedOn: isoDateSchema,
    expiresOn: isoDateSchema.nullable().optional(),
    credentialUrl: certificationUrlSchema.optional(),
    note: tyhjaNulliksi(z.string().trim().max(500).nullable()).optional(),
});
/** Osaamisraportin suodattimet. */
export const certificationReportQuerySchema = z.object({
    certificationId: uuidSchema.optional(),
    provider: z.string().trim().max(SERTIFIKAATTI_TARJOAJA_MAX).optional(),
    /** `true` = vain voimassa olevat; oletuksena myös vanhentuneet. */
    validOnly: booleanQuery,
    includeInactiveUsers: booleanQuery,
});
// --- Kehityssuunnitelma (docs/KEHITYSSUUNNITELMAT.md, luvut 7–8) -----------
export const developmentGoalStatusSchema = z.enum(KEHITYSTAVOITE_TILAT);
/**
 * Suunnan teksti. Markdown kuten tehtävän kuvaus.
 *
 * Tyhjä merkkijono on tyhjennys eikä virhe: kentän tyhjentäminen on tapa
 * poistaa suunta, ja lomake lähettää tyhjän kentän tyhjänä merkkijonona.
 */
export const developmentDirectionSchema = z.object({
    direction: tyhjaNulliksi(z.string().trim().max(5000).nullable()),
});
const goalTitleSchema = z
    .string()
    .trim()
    .min(3, { message: 'Tavoitteen otsikossa on oltava vähintään 3 merkkiä.' })
    .max(200);
/**
 * Valmiin määritelmä on **pakollinen**, ja se on koko moduulin tärkein
 * validointi.
 *
 * Ilman sitä puolen vuoden päästä käytävä keskustelu on keskustelu
 * fiiliksestä: "miten Azure-opiskelu on sujunut" ei ole kysymys, johon on
 * oikea vastaus. Kymmenen merkin alaraja ei estä huonoa määritelmää,
 * mutta se estää tyhjän ja pisteen.
 */
const goalDoneCriteriaSchema = z
    .string()
    .trim()
    .min(10, { message: 'Kerro, mistä tiedätte keskustelussa, että tavoite on saavutettu.' })
    .max(1000);
/**
 * Miltä kehityspolun askeleelta tavoite syntyi (luku 16).
 *
 * Validoidaan muoto, **ei olemassaoloa**: polku elää koodissa, ja
 * tavoitteen luonnin torjuminen siksi, että luettelosta on poistettu
 * askel, olisi sääntö, joka rikkoutuu julkaisussa eikä kirjoitettaessa.
 * Tuntematon koodi on näytöllä pelkkä teksti.
 */
const pathCodeSchema = z
    .string()
    .trim()
    .regex(KEHITYSPOLKU_KOODI_PATTERN, { message: 'Polun koodi ei kelpaa.' });
const pathStepKeySchema = z
    .string()
    .trim()
    .regex(KEHITYSPOLKU_ASKEL_PATTERN, { message: 'Polun askelavain ei kelpaa.' });
export const developmentGoalCreateSchema = z
    .object({
    title: goalTitleSchema,
    doneCriteria: goalDoneCriteriaSchema,
    description: notesSchema,
    targetOn: isoDateSchema,
    certificationId: uuidSchema.nullable().optional(),
    trainingId: uuidSchema.nullable().optional(),
    pathCode: pathCodeSchema.nullable().optional(),
    pathStepKey: pathStepKeySchema.nullable().optional(),
})
    .refine((value) => !(value.certificationId && value.trainingId), {
    message: 'Tavoite voi osoittaa joko sertifikaattiin tai koulutukseen, ei molempiin.',
    path: ['certificationId'],
})
    /**
     * Polkujälki on pari tai ei mitään: pelkkä koodi ilman askelta ei
     * kerro, mistä kohtaa polkua tavoite tuli, eikä pelkkä askel ilman
     * koodia kerro mistä polusta. Kumpikin yksinään olisi rivi, jota ei voi
     * näyttää eikä laskea.
     */
    .refine((value) => Boolean(value.pathCode) === Boolean(value.pathStepKey), {
    message: 'Polun koodi ja askel annetaan yhdessä.',
    path: ['pathStepKey'],
});
/**
 * Tavoitteen muokkaus.
 *
 * `status` **ei ole** täällä. Tila todetaan keskustelussa
 * (`PUT /reviews/:id/checks/:goalId`) tai saavuttamispäätteellä, joka
 * kirjaa samalla sertifikaatin — vapaa tilanvaihto ohittaisi molemmat ja
 * jättäisi rekisterin jälkeen tavoitteista.
 */
export const developmentGoalUpdateSchema = z
    .object({
    title: goalTitleSchema,
    doneCriteria: goalDoneCriteriaSchema,
    description: notesSchema,
    targetOn: isoDateSchema,
    certificationId: uuidSchema.nullable(),
    trainingId: uuidSchema.nullable(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' })
    .refine((value) => !(value.certificationId && value.trainingId), {
    message: 'Tavoite voi osoittaa joko sertifikaattiin tai koulutukseen, ei molempiin.',
    path: ['certificationId'],
});
/**
 * Tavoitteen saavuttaminen.
 *
 * Sertifikaattitavoitteella nämä kentät menevät samassa transaktiossa
 * osaamisrekisteriin (luku 7): rekisteri ei voi olla jäljessä
 * tavoitteista. Muulla tavoitteella vain `achievedOn` on merkitsevä.
 */
export const developmentGoalAchieveSchema = z.object({
    achievedOn: isoDateSchema,
    expiresOn: isoDateSchema.nullable().optional(),
    credentialUrl: certificationUrlSchema.optional(),
    note: tyhjaNulliksi(z.string().trim().max(500).nullable()).optional(),
});
/** Tavoitteen hylkäys. Perustelu on kommentti seuraavaan keskusteluun, ei kenttä. */
export const developmentGoalDropSchema = z.object({
    /** Vapaaehtoinen syy, joka tallentuu tavoitteen kuvaukseen jatkoksi. */
    reason: tyhjaNulliksi(z.string().trim().max(500).nullable()).optional(),
});
export const developmentReviewCreateSchema = z.object({
    heldOn: isoDateSchema,
});
export const developmentReviewUpdateSchema = z
    .object({
    heldOn: isoDateSchema,
    summary: notesSchema,
    nextReviewOn: isoDateSchema.nullable(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Ei muutettavaa.' });
/**
 * Yhden tavoitteen tilannekuva avoimessa keskustelussa.
 *
 * `targetOn` ei ole syötettä: tilannekuva jäädyttää sen, mikä tavoitteella
 * **on** kirjaushetkellä. Jos päivää halutaan siirtää, se siirretään
 * tavoitteelta, ja tilannekuva poimii uuden arvon.
 */
export const developmentGoalCheckSchema = z.object({
    status: developmentGoalStatusSchema,
    comment: tyhjaNulliksi(z.string().trim().max(2000).nullable()).optional(),
});
//# sourceMappingURL=schemas.js.map